import React, { useState, useEffect } from 'react';
import { X, Users, Eye, MousePointerClick, Mail, AlertCircle, CheckCircle2, Clock, Loader2, ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import axios from 'axios';
import { format } from 'date-fns';
import { formatInZone } from '../../lib/timezone';
import useDisplayZone from '../../hooks/useDisplayZone';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OUTCOME_COLORS = {
    sent: 'bg-blue-100 text-blue-700',
    opened: 'bg-emerald-100 text-emerald-700',
    clicked: 'bg-teal-100 text-teal-700',
    completed: 'bg-emerald-100 text-emerald-700',
    branched: 'bg-purple-100 text-purple-700',
    failed: 'bg-red-100 text-red-700',
    skipped: 'bg-stone-100 text-stone-500',
    waiting: 'bg-amber-100 text-amber-700',
};

function formatDate(d, zone) {
    if (!d) return '—';
    // #541: route absolute timestamps through the shared tz-aware formatter
    // in the viewer's profile zone (browser fallback). The zone-less call this
    // replaces rendered UTC despite the comment claiming otherwise.
    try { return formatInZone(d, { zone, formatStr: 'MMM d, h:mm a (zzz)' }) || d; } catch { return d; }
}

export default function NodeExecutionPanel({ stepId, mode: initialMode, campaignId, steps, onClose }) {
    const [mode, setMode] = useState(initialMode || 'passed');
    const displayZone = useDisplayZone();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const pageSize = 25;

    const step = (steps || []).find(s => s.step_id === stepId);
    const stepLabel = step?.label || step?.step_type || 'Step';

    useEffect(() => {
        setPage(1);
    }, [mode, stepId]);

    useEffect(() => {
        if (!stepId || !campaignId) return;
        const fetch = async () => {
            setLoading(true);
            try {
                const token = localStorage.getItem('crm_token');
                const res = await axios.get(`${API}/campaigns/${campaignId}/step/${stepId}/contacts`, {
                    params: { mode, page, page_size: pageSize },
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                });
                setData(res.data);
            } catch (err) {
                console.error('Failed to load step contacts:', err);
                setData(null);
            } finally {
                setLoading(false);
            }
        };
        fetch();
    }, [stepId, campaignId, mode, page]);

    const contacts = data?.contacts || [];
    const total = data?.total || 0;
    const summary = data?.outcome_summary || {};
    const totalPages = Math.ceil(total / pageSize);

    return (
        <div className="w-80 bg-white border-l border-stone-200 flex flex-col h-full overflow-hidden">
            {/* Header */}
            <div className="px-4 py-3 border-b border-stone-200 flex-shrink-0">
                <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {stepLabel}
                    </h3>
                    <button onClick={onClose} className="p-1 text-stone-400 hover:text-stone-600 rounded">
                        <X className="w-4 h-4" />
                    </button>
                </div>
                {/* Mode toggle */}
                <div className="flex bg-stone-100 rounded-lg p-0.5">
                    <button
                        onClick={() => setMode('current')}
                        className={`flex-1 text-[10px] font-medium py-1.5 px-2 rounded-md transition-colors ${
                            mode === 'current' ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-700'
                        }`}
                    >
                        Currently Here
                    </button>
                    <button
                        onClick={() => setMode('passed')}
                        className={`flex-1 text-[10px] font-medium py-1.5 px-2 rounded-md transition-colors ${
                            mode === 'passed' ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-700'
                        }`}
                    >
                        All Who Passed
                    </button>
                </div>
            </div>

            {/* Outcome Summary */}
            {Object.keys(summary).length > 0 && (
                <div className="px-4 py-2 border-b border-stone-100 flex flex-wrap gap-1.5">
                    {Object.entries(summary).map(([outcome, count]) => (
                        <span key={outcome} className={`text-[9px] font-medium px-2 py-0.5 rounded-full ${OUTCOME_COLORS[outcome] || 'bg-stone-100 text-stone-500'}`}>
                            {outcome}: {count}
                        </span>
                    ))}
                </div>
            )}

            {/* Contact List */}
            <div className="flex-1 overflow-y-auto">
                {loading ? (
                    <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-5 h-5 animate-spin text-stone-400" />
                    </div>
                ) : contacts.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-stone-400">
                        <Users className="w-8 h-8 mb-2 opacity-50" />
                        <p className="text-xs">No contacts {mode === 'current' ? 'currently at' : 'have passed through'} this step</p>
                    </div>
                ) : (
                    <div className="divide-y divide-stone-50">
                        {contacts.map((c, i) => (
                            <div key={c.contact_id || i} className="px-4 py-2.5 hover:bg-stone-50 transition-colors">
                                <div className="flex items-center justify-between mb-0.5">
                                    <a href={`/contacts/${c.contact_id}`} className="text-xs font-medium text-stone-900 hover:text-blue-600 flex items-center gap-1">
                                        {c.contact_name || (c.contact_email ? c.contact_email.split('@')[0] : 'Unknown')}
                                        <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-50" />
                                    </a>
                                    {c.step_outcome && (
                                        <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${OUTCOME_COLORS[c.step_outcome] || 'bg-stone-100 text-stone-500'}`}>
                                            {c.step_outcome}
                                        </span>
                                    )}
                                </div>
                                <p className="text-[10px] text-stone-400">{c.contact_email}</p>
                                <div className="flex items-center gap-3 mt-1">
                                    {c.step_entered_at && (
                                        <span className="text-[9px] text-stone-400 flex items-center gap-0.5">
                                            <Clock className="w-2.5 h-2.5" /> {formatDate(c.step_entered_at, displayZone)}
                                        </span>
                                    )}
                                    {c.status && (
                                        <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${
                                            c.status === 'active' ? 'bg-blue-50 text-blue-600' :
                                            c.status === 'completed' ? 'bg-emerald-50 text-emerald-600' :
                                            'bg-stone-50 text-stone-500'
                                        }`}>
                                            {c.status}
                                        </span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="px-4 py-2 border-t border-stone-200 flex items-center justify-between flex-shrink-0">
                    <span className="text-[10px] text-stone-400">{total} contact{total !== 1 ? 's' : ''}</span>
                    <div className="flex items-center gap-1">
                        <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                            className="p-1 text-stone-400 hover:text-stone-600 disabled:opacity-30">
                            <ChevronLeft className="w-3.5 h-3.5" />
                        </button>
                        <span className="text-[10px] text-stone-500">{page}/{totalPages}</span>
                        <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                            className="p-1 text-stone-400 hover:text-stone-600 disabled:opacity-30">
                            <ChevronRight className="w-3.5 h-3.5" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
